import { useEffect, useRef, useState } from 'react';
import { siteConfig } from '../../mocks/siteData';

const FORM_URL = 'https://readdy.ai/api/form/d71v0pohb0rqicn68on0';

const eventTypes = [
  'Auto Show',
  'Press / Media Launch',
  'New Model Reveal',
  'Manufacturer Tour',
  'Fleet Maintenance Program',
  'Golf Tournament / Sporting Event',
  'Display Property Program',
  'Dealer Group Program',
  'Other',
];

const serviceOptions = [
  'Auto Event Management',
  'Press & New Model Launches',
  'Fleet Detailing & Maintenance',
  'Auto Show & Display Support',
  'Manufacturer Tours',
  'Golf & Sporting Events',
  'Nationwide Logistics',
];

interface FinalCTAProps {
  headline?: string;
  subheadline?: string;
}

export default function FinalCTA({
  headline = 'Ready to Elevate Your Program?',
  subheadline = 'Tell us about your event or program and we\'ll build a custom support plan. OTT deploys nationwide — no event too large, no market too far.',
}: FinalCTAProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [charCount, setCharCount] = useState(0);
  const [selectedServices, setSelectedServices] = useState<string[]>([]);

  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold: 0.1 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  const toggleService = (s: string) => {
    setSelectedServices((prev) =>
      prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]
    );
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus('submitting');
    const form = e.currentTarget;
    const data = new FormData(form);
    data.set('services_needed', selectedServices.join(', '));
    const body = new URLSearchParams();
    data.forEach((v, k) => body.append(k, v as string));
    try {
      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (res.ok) {
        setStatus('success');
        form.reset();
        setSelectedServices([]);
        setCharCount(0);
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <section
      ref={ref}
      className="py-28 px-6 lg:px-12 xl:px-16 relative overflow-hidden"
      style={{ background: 'linear-gradient(135deg, #0a0908 0%, #0e0e0e 100%)' }}
    >
      {/* Decorative */}
      <div className="absolute top-0 left-0 w-96 h-96 rounded-full bg-gold/3 blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-64 h-64 rounded-full bg-gold/5 blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">

          {/* Left — Headline */}
          <div className={`transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="flex items-center gap-3 mb-5">
              <span className="w-10 h-px bg-gold" />
              <span className="text-gold text-[10px] font-sans tracking-[0.35em] uppercase font-semibold">Get a Custom Quote</span>
            </div>
            <h2 className="font-serif text-white text-4xl lg:text-5xl font-bold leading-tight mb-6">
              {headline}
            </h2>
            <p className="text-white/45 text-base font-sans leading-relaxed mb-10">
              {subheadline}
            </p>

            {/* Trust indicators */}
            <div className="flex flex-col gap-4 mb-10">
              {[
                { icon: 'ri-earth-line', text: 'Nationwide deployment — all 50 states' },
                { icon: 'ri-calendar-event-line', text: 'Auto shows, press launches, tours & more' },
                { icon: 'ri-time-line', text: 'Programs typically respond within 24 hours' },
                { icon: 'ri-shield-star-line', text: 'Trusted by OEMs, agencies & dealer groups' },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-8 h-8 flex items-center justify-center border border-gold/20 bg-gold/5">
                    <i className={`${item.icon} text-gold text-sm`} />
                  </div>
                  <span className="text-white/50 text-sm font-sans">{item.text}</span>
                </div>
              ))}
            </div>

            {/* Phone CTA */}
            <a
              href={siteConfig.phoneHref}
              className="inline-flex items-center gap-4 group cursor-pointer"
            >
              <div className="w-12 h-12 flex items-center justify-center border border-gold/40 bg-gold/8 group-hover:bg-gold/20 transition-colors">
                <i className="ri-phone-fill text-gold text-lg" />
              </div>
              <div>
                <div className="text-[10px] text-white/30 tracking-widest uppercase font-sans mb-0.5">Call Directly</div>
                <div className="text-gold text-lg font-sans font-semibold">{siteConfig.phone}</div>
              </div>
            </a>
          </div>

          {/* Right — Form */}
          <div className={`transition-all duration-700 delay-200 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            {status === 'success' ? (
              <div className="flex flex-col items-center justify-center h-full min-h-96 text-center p-12 border border-gold/20 bg-gold/5">
                <div className="w-16 h-16 flex items-center justify-center border border-gold/40 mb-6">
                  <i className="ri-check-line text-gold text-2xl" />
                </div>
                <h3 className="font-serif text-white text-2xl font-semibold mb-3">Request Received</h3>
                <p className="text-white/45 text-sm font-sans leading-relaxed max-w-xs">
                  We'll review your program details and be in touch within 24 hours to discuss next steps.
                </p>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                data-readdy-form
                className="flex flex-col gap-5"
              >
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-white/40 text-[11px] font-sans tracking-widest uppercase mb-2">First Name *</label>
                    <input
                      name="first_name"
                      type="text"
                      required
                      placeholder="First"
                      className="w-full bg-white/5 border border-white/10 text-white text-sm font-sans px-4 py-3 outline-none focus:border-gold/50 transition-colors placeholder:text-white/20"
                    />
                  </div>
                  <div>
                    <label className="block text-white/40 text-[11px] font-sans tracking-widest uppercase mb-2">Last Name *</label>
                    <input
                      name="last_name"
                      type="text"
                      required
                      placeholder="Last"
                      className="w-full bg-white/5 border border-white/10 text-white text-sm font-sans px-4 py-3 outline-none focus:border-gold/50 transition-colors placeholder:text-white/20"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-white/40 text-[11px] font-sans tracking-widest uppercase mb-2">Company / Organization *</label>
                  <input
                    name="company"
                    type="text"
                    required
                    placeholder="Brand, Agency, or Dealer Group"
                    className="w-full bg-white/5 border border-white/10 text-white text-sm font-sans px-4 py-3 outline-none focus:border-gold/50 transition-colors placeholder:text-white/20"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-white/40 text-[11px] font-sans tracking-widest uppercase mb-2">Email *</label>
                    <input
                      name="email"
                      type="email"
                      required
                      placeholder="you@company.com"
                      className="w-full bg-white/5 border border-white/10 text-white text-sm font-sans px-4 py-3 outline-none focus:border-gold/50 transition-colors placeholder:text-white/20"
                    />
                  </div>
                  <div>
                    <label className="block text-white/40 text-[11px] font-sans tracking-widest uppercase mb-2">Phone</label>
                    <input
                      name="phone"
                      type="tel"
                      placeholder="(000) 000-0000"
                      className="w-full bg-white/5 border border-white/10 text-white text-sm font-sans px-4 py-3 outline-none focus:border-gold/50 transition-colors placeholder:text-white/20"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-white/40 text-[11px] font-sans tracking-widest uppercase mb-2">Event Type</label>
                    <select
                      name="event_type"
                      className="w-full bg-white/5 border border-white/10 text-white/70 text-sm font-sans px-4 py-3 outline-none focus:border-gold/50 transition-colors cursor-pointer"
                    >
                      <option value="">Select Type</option>
                      {eventTypes.map((t) => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-white/40 text-[11px] font-sans tracking-widest uppercase mb-2">Number of Vehicles</label>
                    <input
                      name="number_of_vehicles"
                      type="text"
                      placeholder="e.g. 10–25"
                      className="w-full bg-white/5 border border-white/10 text-white text-sm font-sans px-4 py-3 outline-none focus:border-gold/50 transition-colors placeholder:text-white/20"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-white/40 text-[11px] font-sans tracking-widest uppercase mb-2">Event Location</label>
                    <input
                      name="event_location"
                      type="text"
                      placeholder="City, State or Nationwide"
                      className="w-full bg-white/5 border border-white/10 text-white text-sm font-sans px-4 py-3 outline-none focus:border-gold/50 transition-colors placeholder:text-white/20"
                    />
                  </div>
                  <div>
                    <label className="block text-white/40 text-[11px] font-sans tracking-widest uppercase mb-2">Event Dates</label>
                    <input
                      name="event_dates"
                      type="text"
                      placeholder="e.g. Sept 15–18, 2026"
                      className="w-full bg-white/5 border border-white/10 text-white text-sm font-sans px-4 py-3 outline-none focus:border-gold/50 transition-colors placeholder:text-white/20"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-white/40 text-[11px] font-sans tracking-widest uppercase mb-3">Services Needed</label>
                  <div className="flex flex-wrap gap-2">
                    {serviceOptions.map((s) => (
                      <button
                        key={s}
                        type="button"
                        onClick={() => toggleService(s)}
                        className={`text-xs font-sans px-3 py-2 border transition-all duration-150 cursor-pointer whitespace-nowrap ${
                          selectedServices.includes(s)
                            ? 'border-gold/60 bg-gold/15 text-gold'
                            : 'border-white/10 bg-white/5 text-white/40 hover:border-white/25 hover:text-white/60'
                        }`}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-white/40 text-[11px] font-sans tracking-widest uppercase mb-2">
                    Additional Notes
                    <span className="text-white/20 ml-2 normal-case tracking-normal">({charCount}/500)</span>
                  </label>
                  <textarea
                    name="additional_notes"
                    rows={3}
                    maxLength={500}
                    placeholder="Program details, special requirements, timeline context..."
                    onChange={(e) => setCharCount(e.target.value.length)}
                    className="w-full bg-white/5 border border-white/10 text-white text-sm font-sans px-4 py-3 outline-none focus:border-gold/50 transition-colors placeholder:text-white/20 resize-none"
                  />
                </div>

                {status === 'error' && (
                  <p className="text-red-400 text-xs font-sans">Something went wrong. Please try again or call us directly.</p>
                )}

                <button
                  type="submit"
                  disabled={status === 'submitting'}
                  className="bg-gold hover:bg-gold-light text-[#0A0A0A] font-sans font-semibold text-sm px-8 py-4 uppercase tracking-widest transition-colors duration-200 cursor-pointer disabled:opacity-60 whitespace-nowrap mt-2"
                >
                  {status === 'submitting' ? 'Submitting...' : 'Get a Custom Event Quote'}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
